import { createSelector } from 'reselect';
import { isEmpty } from 'ramda';
import memoize from 'lodash/memoize';
import get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { max } from 'date-fns';

import { selectors as coreSelectors } from 'mage-core';
import { translator, selectors as i18nSelectors } from 'mage-i18n';
import { MOET_CONSTANTS } from 'moet-core';

import { APP_PATHS, COUNTRIES_TO_UPDATE_TOC } from '@/constants';
import { getTermsAndConditionsForCountry } from '@/utils/termsAndConditions';

import {
  getMerchantIdFromWidgetCookie,
  getOnboardingTypeFromWidgetCookie,
  isWidget,
  isUnmanagedKyc,
} from '../widget';
import { appState } from '../utils';
import { getEntityTypes } from '../entityTypes';
import { getOrganizationEntityTypes } from '../organizationEntityTypes';
import { getCompatibleAndSortedProducts, getSelectedProductId } from '../products';
import { getCountries, getCountryConfig, getRegionConfig } from '../options';
import { getCurrentRegion, getSelectedCountryKey } from '../data';
import { hasSelectedMid } from '../midSelector';
import * as dateUtils from '@/utils/date';

const { ONBOARDING_STATUS_VALUES_V2, PRODUCT_PRICING_TYPES, ONBOARDING_TYPE } = MOET_CONSTANTS;

export const merchantNameSpace = createSelector(appState, ({ merchant = {} }) => merchant);

export const isMerchantFound = createSelector(
  merchantNameSpace,
  ({ foundMerchant }) => foundMerchant !== false,
);

export const getNeedsMerchantRefresh = createSelector(
  merchantNameSpace,
  ({ needsMerchantRefresh }) => needsMerchantRefresh,
);

export const getNeedsMerchantRefreshCount = createSelector(
  merchantNameSpace,
  ({ needsMerchantRefreshCount }) => needsMerchantRefreshCount,
);

export const getNeedsProductRefresh = createSelector(
  merchantNameSpace,
  ({ needsProductRefresh }) => needsProductRefresh,
);

export const hasPhoneNumber = createSelector(
  merchantNameSpace,
  ({ contactPhoneNumber, data: { phoneNumber } = {} }) => !!contactPhoneNumber || !!phoneNumber,
);

export const getUserMerchantId = createSelector(
  coreSelectors.getSelectedMid,
  getMerchantIdFromWidgetCookie,
  (coreMid, widgetMid) => coreMid || widgetMid,
);

export const getMerchant = createSelector(merchantNameSpace, ({ data = {} }) => data);

export const getMerchantError = createSelector(merchantNameSpace, ({ error }) => error);

export const getMerchantCountryKey = createSelector(getMerchant, ({ country }) => country);
export const getMerchantCountry = createSelector(
  getMerchantCountryKey,
  getCountries,
  (countryKey, countries) => countries.find(country => country.key === countryKey),
);

export const getMerchantCountryTranslated = createSelector(
  getMerchantCountryKey,
  translator,
  (countryKey, t) => t(`products-fe.countries.${countryKey}`),
);

export const getMerchantCountryCurrency = createSelector(getMerchantCountry, country =>
  get(country, `data.currencies[0]`, {}),
);

export const getBusinessDetails = createSelector(
  getMerchant,
  ({ businessDetails = {} }) => businessDetails,
);

export const isMerchantRegistered = createSelector(
  getBusinessDetails,
  ({ registered }) => !!registered,
);

export const getMerchantStatus = createSelector(getMerchant, ({ status }) => status);

export const getMerchantStatusValue = createSelector(getMerchantStatus, onboardingStatus =>
  onboardingStatus ? ONBOARDING_STATUS_VALUES_V2[onboardingStatus] : null,
);

export const isMerchantOnboardingCompleted = createSelector(
  getMerchantStatusValue,
  onboardingStatus => onboardingStatus === ONBOARDING_STATUS_VALUES_V2.COMPLETED,
);

export const isMerchantOnboardingRejected = createSelector(
  getMerchantStatusValue,
  onboardingStatus =>
    onboardingStatus === ONBOARDING_STATUS_VALUES_V2.SUSPENDED ||
    onboardingStatus === ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_DISABLED,
);

export const isMerchantInStatus = expectedStatusValue =>
  createSelector(getMerchantStatusValue, statusValue => statusValue === expectedStatusValue);

export const hasMerchantSubmittedBusinessDetails = createSelector(
  getMerchantStatusValue,
  onboardingStatus => onboardingStatus >= ONBOARDING_STATUS_VALUES_V2.TRANSACTION_DETAILS_STARTED,
);

export const isMerchantInReview = createSelector(
  getMerchantStatusValue,
  onboardingStatus =>
    onboardingStatus > ONBOARDING_STATUS_VALUES_V2.TRANSACTION_DETAILS_STARTED &&
    onboardingStatus < ONBOARDING_STATUS_VALUES_V2.COMPLETED,
);

export const isMerchantApprovedForTransactions = createSelector(
  getMerchantStatusValue,
  onboardingStatus => onboardingStatus >= ONBOARDING_STATUS_VALUES_V2.TRANSACTIONS_ENABLED,
);

export const doesMerchantNeedToUpdateToc = createSelector(getMerchantCountryKey, country =>
  COUNTRIES_TO_UPDATE_TOC.includes(country),
);

export const getFirstOrderAt = createSelector(
  merchantNameSpace,
  ({ data, firstOrderAt }) => firstOrderAt || data?.timestamps?.firstLiveTransactionAt,
);

export const hasHadTransaction = createSelector(getFirstOrderAt, firstOrderAt => !!firstOrderAt);

export const getTransactionsRequestedAt = createSelector(
  getMerchant,
  ({ timestamps = {} }) => timestamps.transactionsRequestedAt,
);

export const getPayoutsRequestedAt = createSelector(
  getMerchant,
  ({ timestamps = {} }) => timestamps.payoutsRequestedAt,
);

export const getEntityTypesInMerchantCountry = createSelector(
  getMerchantCountryKey,
  getEntityTypes,
  (country, entityTypes = {}) => entityTypes[country] || entityTypes.DEFAULT,
);

export const getOrganizationEntityTypesInMerchantCountry = createSelector(
  getMerchantCountryKey,
  getOrganizationEntityTypes,
  (country, organizationEntityTypes = {}) => {
    return organizationEntityTypes[country] || organizationEntityTypes.DEFAULT;
  },
);
export const getEntityTypesAvailableForMerchant = createSelector(
  isMerchantRegistered,
  getEntityTypesInMerchantCountry,
  (isMerchantRegistered, entityTypes = []) =>
    entityTypes
      .filter(({ registered }) => registered === null || isMerchantRegistered === registered)
      .map(({ registered, ...rest }) => rest),
);

export const getDataForApplicationConfirmation = createSelector(
  getMerchant,
  getCompatibleAndSortedProducts,
  translator,
  (
    {
      country: merchantCountryKey,
      businessDetails: allBusinessDetails = {},
      stakeholders = [],
      products: merchantProducts = [],
      ...rest
    },
    products = [],
    t,
  ) => {
    const { address = {}, operatingAddress = null, ...businessDetails } = allBusinessDetails;
    return {
      ...rest,
      country: merchantCountryKey,
      businessDetails,
      address:
        address && !isEmpty(address)
          ? {
              ...address,
              country: t(`products-fe.countries.${address.country}`, {}, address.country),
              countryKey: address.country,
            }
          : null,
      operatingAddress:
        operatingAddress && !isEmpty(operatingAddress)
          ? {
              ...operatingAddress,
              country: t(
                `products-fe.countries.${operatingAddress.country}`,
                {},
                operatingAddress.country,
              ),
              countryKey: operatingAddress.country,
            }
          : null,
      stakeholders: stakeholders.map(({ type, roles, data, ...s }) => {
        const country = get(data, 'address.country', '');
        return {
          ...s,
          type,
          translatedType: t(`products-fe.options.stakeholder.types.${type}`, {}, type),
          roles,
          data: {
            ...data,
            address: !_isEmpty(data.address)
              ? {
                  ...data.address,
                  country: t(`products-fe.countries.${country}`, {}, country),
                  countryKey: country,
                }
              : null,
            birthday: get(data, 'dateOfBirth', '')
              .split('-')
              .reverse()
              .join('/'),
          },
        };
      }),
      productsForAcceptance: merchantProducts.map(merchantProduct => {
        const product = products.find(p => p.id === merchantProduct.id);
        let relevantPrices;
        switch (product.pricing) {
          case PRODUCT_PRICING_TYPES.PARENT:
            relevantPrices = [
              {
                ...product,
                price: product.prices.find(price => price.consumerCountry === merchantCountryKey),
              },
            ];
            break;
          case PRODUCT_PRICING_TYPES.CHILDREN: {
            const merchantProductOptions = merchantProduct.options.map(o => o.id);
            relevantPrices = product.options
              .filter(o => merchantProductOptions.includes(o.id))
              .map(o => ({
                ...o,
                price: o.prices.find(price => price.consumerCountry === merchantCountryKey),
              }));
            break;
          }
        }
        const homeMarketPriceAvailable = !relevantPrices.some(p => !p.price);
        const termsAndConditions = getTermsAndConditionsForCountry(
          product.termsAndConditions,
          merchantCountryKey,
        );

        return {
          id: product.id,
          name: product.name,
          description: product.description,
          homeMarketPriceAvailable,
          list: relevantPrices,
          termsAndConditionsLink: termsAndConditions.href,
        };
      }),
    };
  },
);

export const getMerchantStatusCompletedTimeStamp = createSelector(
  getMerchantStatus,
  getMerchant,
  (onboardingStatus, { timestamps }) =>
    onboardingStatus === ONBOARDING_STATUS_VALUES_V2.COMPLETED && timestamps.lastModifiedAt
      ? timestamps.lastModifiedAt
      : null,
);

const getAcceptedTocTimestamp = createSelector(
  getMerchant,
  getSelectedProductId,
  ({ products = [] }, selectedProductId) => {
    const productData = products.find(p => p.id === selectedProductId);

    let tocData = [];

    if (
      productData &&
      (Object.prototype.hasOwnProperty.call(productData, 'tocSource') ||
        Object.prototype.hasOwnProperty.call(productData, 'tocLink'))
    ) {
      tocData = [
        {
          ...productData,
          tocTimestamp: productData.tocAcceptedAt || productData.tocTimestamp,
        },
      ];
    } else if (productData && productData.options) {
      tocData = productData.options
        .filter(option => (option.tocSource || option.tocLink) !== undefined)
        .map(option => ({
          ...option,
          tocTimestamp: option.tocAcceptedAt || option.tocTimestamp,
          mainProductId: productData.id,
        }));
    }

    return tocData;
  },
);

const getAcceptedTocData = createSelector(
  getAcceptedTocTimestamp,
  getMerchant,
  (tocData, { country }) => {
    const formatDate = dateUtils.createDateFormatter(country);

    return tocData.map(d => ({
      ...d,
      tocTimestamp: formatDate(d.tocTimestamp),
    }));
  },
);

export const getTimestampOfLastAcceptedToc = createSelector(getAcceptedTocTimestamp, tocData => {
  return max(tocData.map(d => new Date(d.tocTimestamp)));
});

export const getDataForStoreDetailsSummary = createSelector(
  getMerchant,
  getAcceptedTocData,
  (merchant, tocData = {}) => {
    const storeDetails = {
      ...merchant,
      tocData,
    };
    return storeDetails;
  },
);

export const getIsValidWidgetToken = createSelector(
  merchantNameSpace,
  ({ isValidWidgetToken }) => isValidWidgetToken,
);

export const isManagedMerchant = createSelector(
  getOnboardingTypeFromWidgetCookie,
  onboardingType => onboardingType === ONBOARDING_TYPE.MANAGED,
);

// Return the key for a translation that depends on merchant information.
// Current format is:
// en:
//   some:
//     key:
//       widgetType[managed|unmanaged]:
//         country[country_code|default]:
//
// Where widget type and country are optional. Check keyPriority for the fallback chain
export const getMerchantAwareTranslationKey = createSelector(
  i18nSelectors.translations,
  getMerchantCountryKey,
  getSelectedCountryKey,
  isManagedMerchant,
  (translations, merchantCountry, selectedCountry, isManagedMerchant) =>
    memoize((tKey, country = merchantCountry || selectedCountry) => {
      const widgetTypeSuffix = isManagedMerchant ? 'managed' : 'unmanaged';

      // Get the english tree for the given label
      const mainTranslations = get(translations, `en.${tKey}`);

      // Try to find one of these keys.
      const keyPriority = [
        `${widgetTypeSuffix}.${country}`,
        `${widgetTypeSuffix}.default`,
        `${widgetTypeSuffix}`,
        `${country}`,
        `default`,
        '',
      ];

      let result;
      while (result === undefined && keyPriority.length) {
        const key = keyPriority.shift();
        if (get(mainTranslations, `${key}`)) {
          result = key;
        }
      }
      // Returns the first one found or the key itself
      return `${tKey}${result ? `.${result}` : ''}`;
    }),
);

// Returns a translator that uses merchant information to get the correct key
export const getMerchantAwareTranslator = createSelector(
  translator,
  getMerchantAwareTranslationKey,
  (t, getKey) => (...params) => t(getKey(...params)),
);

// Translates an array of elements with the format
// - text
//   link
// - text
// - text
// .
// .
// .
export const getMerchantAwareArrayTranslator = createSelector(
  i18nSelectors.translations,
  translator,
  getMerchantAwareTranslationKey,
  (translations, t, getKey) => tKey => {
    // Get the key to be used according to english config
    const finalKey = getKey(tKey);
    // Get the english version
    const englishValue = get(translations, `en.${finalKey}`);

    // We check if it's an array here just in case there is no default
    return Array.isArray(englishValue)
      ? englishValue.map(({ link = false, text }, i) => ({
          link,
          // Try to translate  or fall back to english
          text: t(`${finalKey}.${i}.text`) || text,
        }))
      : // as fallback if key is not defined as array, we return a simple translation string
        t(finalKey) || englishValue;
  },
);

export const isNewSplitLayout = createSelector(
  isWidget,
  isUnmanagedKyc,
  getMerchant,
  getTransactionsRequestedAt,
  hasSelectedMid,
  (isWidget, isUnmanagedKyc, merchant, transactionsRequestedAt, hasSelectedMid) =>
    !window.location.pathname?.startsWith('/products/gui') &&
    // If the flow is rerendered and the path is still mid-selector, we can show the split layout
    // when the mid was just selected (If the new mid supports it)
    (window.location.pathname !== `/products/${APP_PATHS.MID_SELECTOR}` || hasSelectedMid) &&
    (!merchant ||
      !merchant.momaStatus ||
      isWidget ||
      (isUnmanagedKyc &&
        merchant.momaStatus.current === 'PENDING' &&
        // do not render split layout on whats next or checking page
        _isEmpty(transactionsRequestedAt))),
);

export const getSelectableMerchants = createSelector(
  merchantNameSpace,
  ({ selectableMerchants: { eu = [], us = [], ap = [] } = {} }) => eu.concat(us).concat(ap),
);

export const merchantRegion = createSelector(
  coreSelectors.getSelectedMid,
  coreSelectors.getMerchantRegion,
  getCurrentRegion,
  (mid, getMerchantRegion, currentRegion) => {
    const region = getMerchantRegion(mid) || currentRegion;
    return region?.toLowerCase ? region.toLowerCase() : region;
  },
);

export const getMerchantRegionConfig = createSelector(
  merchantRegion,
  getRegionConfig,
  (region, regionConfig) => (regionConfig ? regionConfig[region] : undefined),
);

export const getMerchantCountryConfig = createSelector(
  getMerchantCountryKey,
  getSelectedCountryKey,
  getCountryConfig,
  (country, signupCountry, countryConfig = {}) =>
    countryConfig ? countryConfig[country || signupCountry] : undefined,
);

export const getMappedCountryConfig = createSelector(
  getMerchantRegionConfig,
  getMerchantCountryConfig,
  (merchantRegionConfig, merchantCountryConfig) => ({
    ...merchantRegionConfig,
    ...(merchantCountryConfig || { isDefault: true }),
  }),
);

export const getDocuments = createSelector(merchantNameSpace, ({ documents = [] }) => documents);

export const getBusinessDocuments = createSelector(getDocuments, (documents = []) =>
  documents.filter(document => !document.stakeholderId),
);

export const getPricingAnnex = createSelector(
  getMerchantCountryConfig,
  ({ PRICING_ANNEX } = {}) => PRICING_ANNEX,
);

export const getMerchantEntityType = createSelector(
  getMerchant,
  merchant => merchant?.businessDetails?.entityType,
);
